import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarDays } from '@fortawesome/free-solid-svg-icons';
import styled, { css } from 'styled-components';

// ---------------------------------------------------------------------------
// Styled Components
// ---------------------------------------------------------------------------

const Wrapper = styled.div`
  position: relative;
  width: 100%;
`;

const Trigger = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0.45rem;
  padding: 0.3rem 0.34rem 0.3rem 0.45rem;
  border: 1px solid #d7e1ec;
  border-radius: 10px;
  font-family: 'Roboto', sans-serif;
  font-size: 0.95rem;
  font-weight: 600;
  background: #ffffff;
  color: #10293a;
  min-height: 40px;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.06);

  &:focus-within {
    outline: none;
    border-color: #9fd7a2;
    background: #ffffff;
    box-shadow: 0 0 0 3px rgba(159, 215, 162, 0.2);
  }
`;

const InlineInput = styled.input`
  flex: 1 1 auto;
  width: 100%;
  max-width: none;
  min-width: 0;
  border: none;
  background: transparent;
  background-image: none;
  color: #10293a;
  font-size: 0.95rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  min-height: 30px;
  padding: 0 0.1rem;
  font-family: inherit;
  -webkit-appearance: none;
  appearance: none;

  &::-webkit-calendar-picker-indicator {
    display: none;
    -webkit-appearance: none;
    opacity: 0;
    pointer-events: none;
    width: 0;
    margin: 0;
  }

  &::-webkit-clear-button,
  &::-webkit-inner-spin-button {
    display: none;
  }

  &:focus {
    outline: none;
  }

  @media (max-width: 1024px) {
    font-size: 16px;
  }
`;

const OpenButton = styled.button`
  width: 34px;
  height: 34px;
  border: 1px solid #d2deea;
  border-radius: 8px;
  background: #f4f8fc;
  color: #1e3a52;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
`;

const TriggerIcon = styled(FontAwesomeIcon)`
  font-size: 0.95rem;
  opacity: 0.78;
  flex-shrink: 0;
`;

const Backdrop = styled.button`
  position: fixed;
  inset: 0;
  border: none;
  background: rgba(15, 23, 42, 0.2);
  z-index: 2147483646;
  cursor: default;
  padding: 0;
  margin: 0;
  opacity: 1;
`;

const Popover = styled.div`
  position: fixed;
  top: 1rem;
  left: 50%;
  transform: translateX(-50%);
  width: min(340px, calc(100vw - 1rem));
  background: #ffffff !important;
  border: 1px solid #d7e1ec;
  border-radius: 12px;
  box-shadow: 0 16px 36px rgba(15, 23, 42, 0.22);
  padding: 0.75rem;
  z-index: 2147483647;
  max-height: calc(100vh - 1rem);
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  opacity: 1 !important;
  mix-blend-mode: normal;
  isolation: isolate;
  backdrop-filter: none;
  filter: none;
  pointer-events: auto;

  @media (max-width: 1024px) {
    position: fixed;
    left: 50%;
    right: auto;
    top: calc(env(safe-area-inset-top, 0px) + 0.4rem);
    width: min(340px, calc(100vw - 0.8rem));
    max-width: calc(100vw - 0.8rem);
    height: auto;
    max-height: calc(100dvh - env(safe-area-inset-top, 0px) - 0.8rem);
    overflow-y: auto;
    overscroll-behavior: contain;
    border-radius: 12px;
    border: 1px solid #d7e1ec;
    z-index: 2147483647;
    padding: 0.5rem;
    transform: translateX(-50%);
    background: #ffffff !important;
    opacity: 1 !important;
    mix-blend-mode: normal;
    backdrop-filter: none;
    filter: none;
  }
`;

const PopoverHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  margin-bottom: 0.55rem;

  strong {
    font-size: 0.95rem;
    color: #0f2b40;
    font-weight: 700;
  }

  @media (max-width: 1024px) {
    margin-bottom: 0.35rem;
  }
`;

const MonthButton = styled.button`
  width: 42px;
  height: 42px;
  border-radius: 8px;
  border: 1px solid #d7e1ec;
  background: #f8fbff;
  color: #1f3b52;
  font-size: 1.35rem;
  font-weight: 700;
  cursor: pointer;
`;

const Weekdays = styled.div`
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 0.25rem;
  margin-bottom: 0.32rem;

  span {
    text-align: center;
    font-size: 0.72rem;
    font-weight: 700;
    color: #64748b;
    text-transform: uppercase;
  }

  @media (max-width: 1024px) {
    margin-bottom: 0.24rem;
    gap: 0.18rem;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 0.25rem;

  @media (max-width: 1024px) {
    gap: 0.18rem;
  }
`;

const DayButton = styled.button`
  min-height: 34px;
  border-radius: 8px;
  border: 1px solid transparent;
  background: #ffffff;
  color: #1f2937;
  font-size: 0.84rem;
  font-weight: 600;
  cursor: pointer;

  ${({ $isOutside }) => $isOutside && css`
    color: #9aa5b1;
  `}

  ${({ $isToday }) => $isToday && css`
    border-color: #4caf50;
    color: #2f8f40;
  `}

  ${({ $isSelected }) => $isSelected && css`
    background: #0f67ce;
    color: #ffffff;
    border-color: #0d55ab;
    box-shadow: 0 4px 10px rgba(15, 103, 206, 0.24);
  `}

  ${({ $isDisabled }) => $isDisabled && css`
    opacity: 0.35;
    cursor: not-allowed;
    box-shadow: none;
  `}

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(76, 175, 80, 0.2);
  }
`;

const sharedActionButtonStyles = css`
  min-height: 34px;
  border: 1px solid #d5deea;
  border-radius: 8px;
  background: #f8fbff;
  color: #1f3b52;
  font-size: 0.78rem;
  font-weight: 700;
  cursor: pointer;
`;

const PopoverFooter = styled.div`
  margin-top: 0.62rem;
  padding-top: 0.58rem;
  border-top: 1px solid #e8eef5;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.4rem;

  button {
    ${sharedActionButtonStyles}
  }

  @media (max-width: 1024px) {
    margin-top: 0.38rem;
    padding-top: 0.36rem;
    gap: 0.3rem;
  }
`;

const CloseRow = styled.div`
  margin-top: 0.48rem;

  button {
    ${sharedActionButtonStyles}
    width: 100%;
    min-height: 36px;
    font-size: 0.8rem;
  }

  @media (max-width: 1024px) {
    margin-top: 0.32rem;
  }
`;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const WEEKDAY_LABELS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
const monthYearFormatter = new Intl.DateTimeFormat('fr-FR', { month: 'long', year: 'numeric' });

const getTodayDateKey = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const toDateKey = (value) => {
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return value;
  }
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return getTodayDateKey();
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const fromDateKey = (dateKey) => {
  if (!dateKey || typeof dateKey !== 'string') return new Date();
  const [year, month, day] = dateKey.split('-').map(Number);
  if (!year || !month || !day) return new Date();
  return new Date(year, month - 1, day);
};

const formatDateShort = (value) => {
  const date = value instanceof Date ? value : fromDateKey(value);
  return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
};

const formatDateForField = (value) => {
  const date = value instanceof Date ? value : fromDateKey(value);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const normalizeDateFieldDraft = (rawValue) => {
  const digits = rawValue.replace(/\D/g, '').slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
};

const parseDateFieldValue = (rawValue) => {
  const digits = rawValue.replace(/\D/g, '');
  if (digits.length !== 8) return null;
  const day = Number(digits.slice(0, 2));
  const month = Number(digits.slice(2, 4));
  const year = Number(digits.slice(4, 8));
  if (!day || !month || !year) return null;
  const parsed = new Date(year, month - 1, day);
  if (
    parsed.getFullYear() !== year
    || parsed.getMonth() !== month - 1
    || parsed.getDate() !== day
  ) return null;
  return toDateKey(parsed);
};

const formatMonthYear = (value) => {
  const date = value instanceof Date ? value : fromDateKey(value);
  const label = monthYearFormatter.format(date);
  return label.charAt(0).toUpperCase() + label.slice(1);
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const SharedDatePicker = ({
  className,
  inputId,
  value,
  onChange,
  ariaLabel = 'Saisir une date',
  dialogLabel = 'Sélecteur de date',
  placeholder = 'JJ/MM/AAAA',
  minDateKey,
  maxDateKey,
  disabled = false,
  allowEmpty = false,
  showQuickActions = true,
}) => {
  const selectedDateKey = useMemo(() => {
    if (allowEmpty && !value) return '';
    return toDateKey(value || new Date());
  }, [allowEmpty, value]);

  const fallbackDateKey = useMemo(() => toDateKey(new Date()), []);
  const selectedOrFallbackDateKey = selectedDateKey || fallbackDateKey;

  const [inputValue, setInputValue] = useState(() => (
    selectedDateKey ? formatDateForField(selectedDateKey) : ''
  ));
  const [isOpen, setIsOpen] = useState(false);
  const [popoverStyle, setPopoverStyle] = useState(null);
  const [calendarViewDate, setCalendarViewDate] = useState(() => {
    const current = fromDateKey(selectedOrFallbackDateKey);
    return new Date(current.getFullYear(), current.getMonth(), 1);
  });

  const wrapperRef = useRef(null);
  const popoverRef = useRef(null);

  const applyDateChange = useCallback((nextDateKey) => {
    if (!nextDateKey || disabled) return;
    let normalizedKey = nextDateKey;
    if (minDateKey && normalizedKey < minDateKey) normalizedKey = minDateKey;
    if (maxDateKey && normalizedKey > maxDateKey) normalizedKey = maxDateKey;
    onChange?.(normalizedKey);
  }, [disabled, maxDateKey, minDateKey, onChange]);

  useEffect(() => {
    setInputValue(selectedDateKey ? formatDateForField(selectedDateKey) : '');
  }, [selectedDateKey]);

  const openDatePicker = useCallback(() => {
    const current = fromDateKey(selectedOrFallbackDateKey);
    setCalendarViewDate(new Date(current.getFullYear(), current.getMonth(), 1));
    setIsOpen(true);
  }, [selectedOrFallbackDateKey]);

  const closeDatePicker = useCallback(() => {
    setIsOpen(false);
    setPopoverStyle(null);
  }, []);

  const toggleDatePicker = useCallback(() => {
    if (isOpen) { closeDatePicker(); return; }
    if (disabled) return;
    openDatePicker();
  }, [closeDatePicker, disabled, isOpen, openDatePicker]);

  const handleInlineDateInputChange = useCallback((event) => {
    const normalizedDraft = normalizeDateFieldDraft(event.target.value);
    setInputValue(normalizedDraft);
    if (allowEmpty && normalizedDraft === '') { onChange?.(''); return; }
    const parsedDateKey = parseDateFieldValue(normalizedDraft);
    if (!parsedDateKey) return;
    applyDateChange(parsedDateKey);
    if (isOpen) { setIsOpen(false); setPopoverStyle(null); }
  }, [allowEmpty, applyDateChange, isOpen, onChange]);

  const handleInlineDateInputBlur = useCallback(() => {
    if (allowEmpty && inputValue.trim() === '') {
      setInputValue('');
      onChange?.('');
      return;
    }
    const parsedDateKey = parseDateFieldValue(inputValue);
    if (!parsedDateKey) {
      setInputValue(selectedDateKey ? formatDateForField(selectedDateKey) : '');
      return;
    }
    const normalizedKey = (() => {
      if (minDateKey && parsedDateKey < minDateKey) return minDateKey;
      if (maxDateKey && parsedDateKey > maxDateKey) return maxDateKey;
      return parsedDateKey;
    })();
    setInputValue(formatDateForField(normalizedKey));
    applyDateChange(normalizedKey);
  }, [allowEmpty, applyDateChange, inputValue, maxDateKey, minDateKey, onChange, selectedDateKey]);

  const shiftCalendarMonth = useCallback((offset) => {
    setCalendarViewDate((prev) => new Date(prev.getFullYear(), prev.getMonth() + offset, 1));
  }, []);

  const selectDateFromCalendar = useCallback((date) => {
    applyDateChange(toDateKey(date));
    closeDatePicker();
  }, [applyDateChange, closeDatePicker]);

  const goToday = useCallback(() => {
    applyDateChange(getTodayDateKey());
    closeDatePicker();
  }, [applyDateChange, closeDatePicker]);

  const goRelativeDay = useCallback((offset) => {
    const nextDate = new Date();
    nextDate.setHours(0, 0, 0, 0);
    nextDate.setDate(nextDate.getDate() + offset);
    applyDateChange(toDateKey(nextDate));
    closeDatePicker();
  }, [applyDateChange, closeDatePicker]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleClickOutside = (event) => {
      if (wrapperRef.current?.contains(event.target)) return;
      if (popoverRef.current?.contains(event.target)) return;
      setIsOpen(false);
    };
    const handleEscape = (event) => { if (event.key === 'Escape') setIsOpen(false); };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    window.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
      window.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const updatePopoverPosition = useCallback(() => {
    if (!isOpen || typeof window === 'undefined') return;

    const triggerElement = wrapperRef.current?.querySelector('.date-picker-trigger-inner');
    const popoverElement = popoverRef.current;
    if (!triggerElement || !popoverElement) return;

    const visualViewport = window.visualViewport;
    const viewportWidth = visualViewport?.width || window.innerWidth;
    const viewportHeight = visualViewport?.height || window.innerHeight;
    const viewportOffsetTop = visualViewport?.offsetTop || 0;
    const triggerRect = triggerElement.getBoundingClientRect();
    const popoverRect = popoverElement.getBoundingClientRect();
    const spacing = 8;

    if (viewportWidth <= 768) {
      const mobileWidth = Math.min(340, Math.max(280, viewportWidth - spacing * 2));
      const centeredTop = viewportOffsetTop + (viewportHeight - popoverRect.height) / 2;
      const minTop = viewportOffsetTop + spacing;
      const maxTop = viewportOffsetTop + viewportHeight - popoverRect.height - spacing;
      const top = Math.max(minTop, Math.min(centeredTop, maxTop));
      setPopoverStyle({
        top: `${Math.round(top)}px`,
        left: '50%',
        right: 'auto',
        bottom: 'auto',
        width: `${Math.round(mobileWidth)}px`,
        maxWidth: `${Math.round(mobileWidth)}px`,
        transform: 'translateX(-50%)',
      });
      return;
    }

    let left = triggerRect.left + triggerRect.width / 2 - popoverRect.width / 2;
    left = Math.max(spacing, Math.min(left, viewportWidth - popoverRect.width - spacing));

    const spaceBelow = viewportHeight - triggerRect.bottom - spacing;
    const spaceAbove = triggerRect.top - spacing;
    let top = triggerRect.bottom + spacing;
    if (spaceBelow < popoverRect.height && spaceAbove > spaceBelow) {
      top = triggerRect.top - popoverRect.height - spacing;
    }
    top = Math.max(spacing, Math.min(top, viewportHeight - popoverRect.height - spacing));

    setPopoverStyle({
      top: `${Math.round(top)}px`,
      left: `${Math.round(left)}px`,
      right: 'auto',
      bottom: 'auto',
      transform: 'none',
    });
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const rafId = window.requestAnimationFrame(updatePopoverPosition);
    const timeoutId = window.setTimeout(updatePopoverPosition, 80);
    const handleReposition = () => updatePopoverPosition();
    const visualViewport = window.visualViewport;

    window.addEventListener('resize', handleReposition);
    window.addEventListener('scroll', handleReposition, true);
    visualViewport?.addEventListener('resize', handleReposition);
    visualViewport?.addEventListener('scroll', handleReposition);

    return () => {
      window.cancelAnimationFrame(rafId);
      window.clearTimeout(timeoutId);
      window.removeEventListener('resize', handleReposition);
      window.removeEventListener('scroll', handleReposition, true);
      visualViewport?.removeEventListener('resize', handleReposition);
      visualViewport?.removeEventListener('scroll', handleReposition);
    };
  }, [calendarViewDate, isOpen, updatePopoverPosition]);

  const todayDateKey = useMemo(() => toDateKey(new Date()), []);

  const calendarDays = useMemo(() => {
    const monthStart = new Date(calendarViewDate.getFullYear(), calendarViewDate.getMonth(), 1);
    const mondayIndex = (monthStart.getDay() + 6) % 7;
    const gridStart = new Date(monthStart);
    gridStart.setDate(monthStart.getDate() - mondayIndex);

    return Array.from({ length: 42 }, (_, index) => {
      const dayDate = new Date(gridStart);
      dayDate.setDate(gridStart.getDate() + index);
      const dayDateKey = toDateKey(dayDate);
      const isDisabled = (minDateKey && dayDateKey < minDateKey) || (maxDateKey && dayDateKey > maxDateKey);
      return {
        date: dayDate,
        dateKey: dayDateKey,
        dayNumber: dayDate.getDate(),
        isCurrentMonth: dayDate.getMonth() === calendarViewDate.getMonth(),
        isSelected: dayDateKey === selectedDateKey,
        isToday: dayDateKey === todayDateKey,
        isDisabled,
      };
    });
  }, [calendarViewDate, maxDateKey, minDateKey, selectedDateKey, todayDateKey]);

  return (
    <Wrapper className={className} ref={wrapperRef}>
      {/* On ajoute une classe CSS native pour permettre à updatePopoverPosition de trouver le trigger */}
      <Trigger className="date-picker-trigger-inner">
        <InlineInput
          id={inputId}
          type="text"
          value={inputValue}
          onChange={handleInlineDateInputChange}
          onBlur={handleInlineDateInputBlur}
          inputMode="numeric"
          autoComplete="off"
          spellCheck={false}
          placeholder={placeholder}
          pattern="[0-9]{2}/[0-9]{2}/[0-9]{4}"
          aria-label={ariaLabel}
          title="Saisir la date (JJ/MM/AAAA)"
          disabled={disabled}
        />
        <OpenButton
          type="button"
          onClick={toggleDatePicker}
          aria-expanded={isOpen}
          aria-haspopup="dialog"
          aria-label="Ouvrir le calendrier"
          disabled={disabled}
        >
          <TriggerIcon icon={faCalendarDays} aria-hidden="true" />
        </OpenButton>
      </Trigger>

      {isOpen && typeof document !== 'undefined' && createPortal(
        <>
          <Backdrop
            type="button"
            aria-label="Fermer le calendrier"
            onClick={closeDatePicker}
          />
          <Popover
            ref={popoverRef}
            role="dialog"
            aria-label={dialogLabel}
            style={popoverStyle || undefined}
          >
            <PopoverHeader>
              <MonthButton
                type="button"
                onClick={() => shiftCalendarMonth(-1)}
                aria-label="Mois précédent"
              >
                ‹
              </MonthButton>
              <strong>{formatMonthYear(calendarViewDate)}</strong>
              <MonthButton
                type="button"
                onClick={() => shiftCalendarMonth(1)}
                aria-label="Mois suivant"
              >
                ›
              </MonthButton>
            </PopoverHeader>

            <Weekdays>
              {WEEKDAY_LABELS.map((label) => (
                <span key={label}>{label}</span>
              ))}
            </Weekdays>

            <Grid>
              {calendarDays.map((day) => (
                <DayButton
                  key={day.dateKey}
                  type="button"
                  $isOutside={!day.isCurrentMonth}
                  $isSelected={day.isSelected}
                  $isToday={day.isToday}
                  $isDisabled={day.isDisabled}
                  onClick={() => selectDateFromCalendar(day.date)}
                  aria-label={formatDateShort(day.date)}
                  aria-current={day.isToday ? 'date' : undefined}
                  aria-pressed={day.isSelected}
                  disabled={day.isDisabled}
                >
                  {day.dayNumber}
                </DayButton>
              ))}
            </Grid>

            {showQuickActions && (
              <PopoverFooter>
                <button type="button" onClick={() => goRelativeDay(-1)}>Hier</button>
                <button type="button" onClick={goToday}>Aujourd'hui</button>
                <button type="button" onClick={() => goRelativeDay(1)}>Demain</button>
              </PopoverFooter>
            )}

            <CloseRow>
              <button type="button" onClick={closeDatePicker}>Fermer</button>
            </CloseRow>
          </Popover>
        </>,
        document.body
      )}
    </Wrapper>
  );
};

export { Wrapper, Trigger, InlineInput, OpenButton, TriggerIcon, Backdrop, Popover, PopoverHeader, MonthButton, Weekdays, Grid, DayButton, sharedActionButtonStyles, PopoverFooter, CloseRow, WEEKDAY_LABELS, monthYearFormatter, getTodayDateKey, toDateKey, fromDateKey, formatDateShort, formatDateForField, normalizeDateFieldDraft, parseDateFieldValue, formatMonthYear, SharedDatePicker }